import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	GraphQLErrorMessage,
	Heading3,
	Input,
	Label,
	Select,
	tokens,
} from '@treams/treams-ui';

import Group from '../../goals/group-selector/Group';
import SingleGroupSelector from '../../goals/group-selector/SingleGroupSelector';

const Header = styled(Flex)`
	align-items: center;
	margin-bottom: ${tokens.gap.group}px;
	padding-bottom: ${tokens.gap.group}px;
	border-bottom: 1px solid ${tokens.border.subtle};
`;

type PersonalInformationProps = {
	create?: boolean;
	user?: graphql.User;
	disableEditing?: boolean;
	onChange?: (data: {
		firstName: string;
		lastName: string;
		activatedAt: Date;
		locale: string;
		groupID: string;
		isExternal: boolean;
		gender?: graphql.Gender;
	}) => void;
};

export default function PersonalInformation({
	create,
	user,
	disableEditing,
	onChange,
}: PersonalInformationProps) {
	const { t } = useTranslation();
	const [editing, setEditing] = useState(create ?? false);
	const hasRewards = useFeature(graphql.FeatureName.Rewards);

	const [update] = graphql.useUpdateUserMutation();
	const [error, attempt, reset] = useErrorBoundary();

	const formik = useFormik({
		initialValues: {
			firstName: user?.firstName!,
			lastName: user?.lastName!,
			activatedAt: user?.activatedAt!,
			locale: user?.locale!,
			groupID: user?.group.id!,
			isExternal: user?.isExternal?.toString() ?? 'false',
			gender: (user?.gender ?? '') as graphql.Gender | '',
		},
		onSubmit: () => {},
	});

	useEffect(() => {
		formik.setValues({
			firstName: user?.firstName!,
			lastName: user?.lastName!,
			activatedAt: user?.activatedAt!,
			locale: user?.locale!,
			groupID: user?.group.id!,
			isExternal: user?.isExternal?.toString() ?? 'false',
			gender: (user?.gender ?? '') as graphql.Gender | '',
		});
	}, [!!user, user?.id]);

	useEffect(() => {
		if (!user) {
			return;
		}

		onChange?.({
			...formik.values,
			isExternal: formik.values.isExternal === 'true',
			gender: formik.values.gender || undefined,
		});
		reset();
	}, [JSON.stringify(formik.values)]);

	useEffect(() => {
		if (error && !editing) {
			setEditing(true);
		}
	}, [!!error, editing]);

	return (
		<FormikProvider value={formik}>
			<Header>
				<Flex flexDirection="column" flexGrow={1} flexShrink={1}>
					<Heading3 mb={0}>{t('settings.employees.personal.title')}</Heading3>
				</Flex>
				{!create && (
					<Box flexGrow={0} flexShrink={0}>
						{user ? (
							editing ? (
								<Button
									type="primary"
									role="button"
									aria-label={t('common.save')}
									title={t('common.save')}
									onClick={attempt(async () => {
										if (!user) {
											return;
										}

										setEditing(false);
										await update({
											variables: {
												input: {
													id: user.id,
													email: user.email,
													firstName: formik.values.firstName!,
													lastName: formik.values.lastName!,
													groupID: formik.values.groupID!,
													locale: formik.values.locale!,
													username: user.username,
													activatedAt: formik.values.activatedAt!,
													localDeactivatedAt: user.localDeactivatedAt,
													externalReferenceID: user.externalReferenceID,
													isExternal: formik.values.isExternal === 'true',
													gender: formik.values.gender || null,
												},
											},
										});
									})}
								/>
							) : (
								<Button
									type="secondary"
									role="button"
									aria-label={t('common.edit')}
									disabled={!user.viewerCanUpdate}
									title={t('common.edit')}
									onClick={() => setEditing(true)}
								/>
							)
						) : (
							<Button.Skeleton />
						)}
					</Box>
				)}
			</Header>
			{error && (
				<Box mb={2}>
					<GraphQLErrorMessage error={error} />
				</Box>
			)}
			<Flex mb={2}>
				<Box mr={2} flexGrow={1} flexShrink={1} flexBasis="50%">
					<Label mb={2}>{t('settings.employees.personal.firstName')}:</Label>
					{user ? (
						<Input.Formik
							key={`${editing}`}
							name="firstName"
							disabled={!editing || disableEditing}
							autoFocus={editing}
						/>
					) : (
						<Input.Skeleton />
					)}
				</Box>
				<Box flexGrow={1} flexShrink={1} flexBasis="50%">
					<Label mb={2}>{t('settings.employees.personal.lastName')}:</Label>
					{user ? (
						<Input.Formik name="lastName" disabled={!editing || disableEditing} />
					) : (
						<Input.Skeleton />
					)}
				</Box>
			</Flex>
			<Flex mb={2}>
				<Box mr={2} flexGrow={1} flexShrink={1} flexBasis="50%">
					<Label mb={2}>{t('settings.employees.personal.startDate')}:</Label>
					{user ? (
						<Input.DatePicker.Formik name="activatedAt" disabled={!editing || disableEditing} />
					) : (
						<Input.Skeleton />
					)}
				</Box>
				<Box flexGrow={1} flexShrink={1} flexBasis="50%">
					<Label mb={2}>{t('settings.employees.personal.language')}:</Label>
					{user ? <Input.Language.Formik name="locale" disabled={!editing} /> : <Input.Skeleton />}
				</Box>
			</Flex>
			<Flex mb={2}>
				<Box mr={2} flexGrow={1} flexShrink={1} flexBasis="50%">
					<Label mb={2}>{t('settings.employees.personal.department')}:</Label>
					{user ? (
						<SingleGroupSelector.Formik
							types={[graphql.GroupType.Department]}
							required
							/** If the group has no external reference ID, we allow editing the group */
							disabled={!editing || !!user.group.externalReferenceID}
							name="groupID"
							canSelect={(group) => group.type === graphql.GroupType.Department}
						/>
					) : (
						<Box mb={2}>
							<Group.Skeleton />
						</Box>
					)}
				</Box>
				<Box flexGrow={1} flexShrink={1} flexBasis="50%">
					<Label mb={2}>{t('settings.employees.personal.external.label')}:</Label>
					{user ? (
						<Select.Formik
							name="isExternal"
							search={false}
							disabled={!editing || disableEditing}
							options={[
								{
									title: t('settings.employees.personal.external.false'),
									value: 'false',
								},
								{
									title: t('settings.employees.personal.external.true'),
									value: 'true',
								},
							]}
						/>
					) : (
						<Select.Skeleton />
					)}
				</Box>
			</Flex>
			{hasRewards && (
				<Flex>
					<Box mr={2} flexGrow={1} flexShrink={1} flexBasis="50%">
						<Label mb={2}>{t('settings.employees.personal.gender')}:</Label>
						{user ? (
							<Select.Formik
								name="gender"
								role="combobox"
								aria-label={t('settings.employees.personal.gender')}
								search={false}
								clearable={editing}
								disabled={!editing}
								placeholder={t('settings.employees.personal.gender.placeholder')}
								options={[
									graphql.Gender.Male,
									graphql.Gender.Female,
									graphql.Gender.Other,
									graphql.Gender.PreferNotToSay,
								].map((gender) => ({
									title: t(`settings.employees.personal.gender.${gender}`),
									value: gender,
								}))}
							/>
						) : (
							<Select.Skeleton />
						)}
					</Box>
					<Box flexGrow={1} flexShrink={1} flexBasis="50%" />
				</Flex>
			)}
		</FormikProvider>
	);
}
